import { Form } from 'antd'
import type { FormInstance } from 'antd'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { fetchPermissionGroups } from '@/api/permission-group.api'
import { useAtomValue } from 'jotai'
import { currentUserAtom } from '@/store/auth.atom'

export interface AccountFormValues {
  id?: string
  username: string
  fullName: string
  email: string
  unitId: string
  phone?: string
  note?: string
  groupPermissionId?: string
  groupPermissionName?: string
  roleName?: string
}

interface AccountFormProps {
  form: FormInstance<AccountFormValues>
  mode: 'create' | 'update'
  showUnitColumn: boolean
  onSubmit: (values: AccountFormValues) => void
  isFetchingPermissions?: boolean
}

const PILL_INPUT =
  'rounded-full !bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'
const TEXTAREA_INPUT =
  'rounded-2xl py-3 !bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'

export function AccountForm({ form, mode, onSubmit }: AccountFormProps) {
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1
  const roleName = Form.useWatch('roleName', form)

  const handleFinish = (values: AccountFormValues) => {
    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, val]) => [
        key,
        typeof val === 'string' ? val.trim() : val,
      ]),
    ) as AccountFormValues

    if (mode === 'create' && !trimmedValues.username && trimmedValues.email) {
      trimmedValues.username = trimmedValues.email
    }

    onSubmit(trimmedValues)
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      requiredMark={(label, info) => (
        <>
          {label}
          {info.required && <span className="text-destructive ml-1">*</span>}
        </>
      )}
      className="space-y-6"
    >
      {/* Thông tin tài khoản */}
      <div className="rounded-xl border border-solid border-gray-300 px-5">
        <h3 className="mb-4 text-lg font-semibold">Thông tin tài khoản</h3>

        <Form.Item
          name="fullName"
          label="Họ và tên"
          rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
        >
          <Input
            placeholder="Nhập họ và tên"
            size="large"
            className={PILL_INPUT}
          />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Số điện thoại"
          rules={[
            {
              pattern: /^[\d\s+()-]+$/,
              message: 'Số điện thoại chỉ được chứa số và các ký tự +, -, (, )',
            },
            {
              max: 15,
              message: 'Số điện thoại không được vượt quá 15 ký tự',
            },
          ]}
        >
          <Input
            type="tel"
            placeholder="Nhập số điện thoại"
            size="large"
            maxLength={15}
            className={PILL_INPUT}
          />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Vui lòng nhập email' },
            { type: 'email', message: 'Email không hợp lệ' },
          ]}
        >
          <Input
            placeholder="Nhập email"
            size="large"
            disabled={mode === 'update'}
            className={PILL_INPUT}
            onBlur={(e: { target: { value: string } }) => {
              if (mode === 'create') {
                form.setFieldsValue({ username: e.target.value.trim() })
              }
            }}
          />
        </Form.Item>

        <Form.Item name="roleName" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="groupPermissionId"
          label="Vai trò"
          rules={[{ required: true, message: 'Vui lòng chọn vai trò' }]}
        >
          <Select
            cacheKey={`permission-select-account-form`}
            initialPage={0}
            lazy
            size="large"
            placeholder="Chọn vai trò"
            allowClear
            className="w-full h-10"
            disabled={roleName === 'ADMIN'}
            enableRemoteSearch
            initialOptions={
              mode === 'update' && form.getFieldValue('groupPermissionName')
                ? [
                    {
                      value: form.getFieldValue('groupPermissionId'),
                      label: form.getFieldValue('groupPermissionName'),
                    },
                  ]
                : []
            }
            loadOptions={async ({ page, signal, searchText }) => {
              const res = await fetchPermissionGroups(
                {
                  unitId: !showUnitColumn ? currentUser?.unitId : undefined,
                  status: 1,
                  page: page,
                  size: 10,
                  keySearch: searchText?.trim() || undefined,
                },
                signal,
              )
              return {
                options: res.map((u: any) => ({
                  value: u.id,
                  label: u.name,
                })),
                hasNextPage: res.length === 10,
                nextPage: page + 1,
              }
            }}
            onLazyValueChange={(val) => {
              form.setFieldsValue({ groupPermissionId: val })
            }}
          />
        </Form.Item>

        <Form.Item name="note" label="Ghi chú">
          <Input.TextArea
            placeholder="Nhập ghi chú"
            size="large"
            autoSize={{ minRows: 4, maxRows: 6 }}
            className={TEXTAREA_INPUT}
          />
        </Form.Item>
      </div>
    </Form>
  )
}
